import { deriveSeed } from "./seed.js";
import type {
  AffinityVector,
  AtlasChoreographyId,
  ChoreographyDefinition,
  ThemeDefinition,
  ThemeId,
} from "../experience/types.js";

const neutralAffinity = (): AffinityVector =>
  Object.freeze({
    light: 0,
    warmth: 0,
    moisture: 0,
    weight: 0,
    fluidity: 0,
    turbulence: 0,
    resonance: 0,
    tension: 0,
  });

const theme = (
  definition: Omit<ThemeDefinition, "affinityScale">,
): ThemeDefinition => Object.freeze({ ...definition, affinityScale: neutralAffinity() });

const choreography = (definition: ChoreographyDefinition): ChoreographyDefinition =>
  Object.freeze(definition);

export const THEME_DEFINITIONS: readonly ThemeDefinition[] = Object.freeze([
  theme({
    id: "sumi-night",
    japaneseMaterial: "sumi ink",
    outsideStyle: "film noir",
    background: "#101d28",
    paper: "#e9e8d5",
    ink: "#172326",
    wash: "#b7c9c7",
    accent: "#b74832",
    grain: 0.32,
  }),
  theme({
    id: "washi-dawn",
    japaneseMaterial: "washi papermaking",
    outsideStyle: "Scandinavian luminism",
    background: "#d9b4a0",
    paper: "#f5e8bd",
    ink: "#27302d",
    wash: "#8a5345",
    accent: "#b74832",
    grain: 0.5,
  }),
  theme({
    id: "kintsugi-ember",
    japaneseMaterial: "kintsugi gold joinery",
    outsideStyle: "German expressionism",
    background: "#241b1b",
    paper: "#382725",
    ink: "#f0dfb7",
    wash: "#d7b76f",
    accent: "#d4a948",
    grain: 0.22,
  }),
  theme({
    id: "urushi-deco",
    japaneseMaterial: "urushi lacquer",
    outsideStyle: "Art Deco",
    background: "#171311",
    paper: "#3a211d",
    ink: "#f1d8aa",
    wash: "#7c3028",
    accent: "#d9a441",
    grain: 0.12,
  }),
  theme({
    id: "shibori-bauhaus",
    japaneseMaterial: "arashi shibori dyeing",
    outsideStyle: "Bauhaus",
    background: "#eadfca",
    paper: "#f7f0df",
    ink: "#183451",
    wash: "#456f91",
    accent: "#d55235",
    grain: 0.42,
  }),
  theme({
    id: "raku-brutalism",
    japaneseMaterial: "raku firing",
    outsideStyle: "Brutalism",
    background: "#2b2b27",
    paper: "#b9b09d",
    ink: "#191a18",
    wash: "#777568",
    accent: "#c8663f",
    grain: 0.68,
  }),
  theme({
    id: "mokume-op-art",
    japaneseMaterial: "mokume-gane metalwork",
    outsideStyle: "Op Art",
    background: "#d7d2c4",
    paper: "#f4f0e5",
    ink: "#25282a",
    wash: "#8d8c86",
    accent: "#bd4b35",
    grain: 0.27,
  }),
  theme({
    id: "aizome-cyanotype",
    japaneseMaterial: "aizome indigo dyeing",
    outsideStyle: "Victorian cyanotype",
    background: "#092847",
    paper: "#d9e7df",
    ink: "#0d3152",
    wash: "#4f83a6",
    accent: "#d7bc72",
    grain: 0.46,
  }),
  theme({
    id: "kumiko-constructivism",
    japaneseMaterial: "kumiko joinery",
    outsideStyle: "Russian constructivism",
    background: "#d6c5a4",
    paper: "#f3e5c8",
    ink: "#24201b",
    wash: "#92785a",
    accent: "#c93d2d",
    grain: 0.3,
  }),
  theme({
    id: "setoyaki-pop",
    japaneseMaterial: "Seto-yaki glazing",
    outsideStyle: "Pop Art",
    background: "#f0c94c",
    paper: "#f7edcf",
    ink: "#20365d",
    wash: "#4c8fa5",
    accent: "#d94a42",
    grain: 0.18,
  }),
  theme({
    id: "sashiko-modernism",
    japaneseMaterial: "sashiko stitching",
    outsideStyle: "Swiss modernism",
    background: "#15364b",
    paper: "#ece5d5",
    ink: "#172c39",
    wash: "#6e8997",
    accent: "#db553e",
    grain: 0.38,
  }),
  theme({
    id: "kirigami-surrealism",
    japaneseMaterial: "kirigami paper cutting",
    outsideStyle: "Surrealism",
    background: "#b8c6b2",
    paper: "#f2e7cd",
    ink: "#303b35",
    wash: "#7d9889",
    accent: "#b8553d",
    grain: 0.35,
  }),
  theme({
    id: "tatami-midcentury",
    japaneseMaterial: "igusa tatami weaving",
    outsideStyle: "mid-century modern",
    background: "#8b8b64",
    paper: "#e8dfba",
    ink: "#2e3b32",
    wash: "#9e774d",
    accent: "#c65435",
    grain: 0.58,
  }),
]);

export const CHOREOGRAPHY_DEFINITIONS: readonly ChoreographyDefinition[] = Object.freeze([
  choreography({ id: "solitude", intensity: 0.15, parallax: 0.08, particleDensity: 0.1, durationMs: 9000, staggerMs: 900, drift: 0.08, spread: 0.18 }),
  choreography({ id: "courtyard", intensity: 0.5, parallax: 0.4, particleDensity: 0.45, durationMs: 6000, staggerMs: 420, drift: 0.32, spread: 0.48 }),
  choreography({ id: "dream", intensity: 0.9, parallax: 0.85, particleDensity: 0.9, durationMs: 11000, staggerMs: 180, drift: 0.82, spread: 0.9 }),
  choreography({ id: "stillness", intensity: 0.05, parallax: 0.02, particleDensity: 0.06, durationMs: 14000, staggerMs: 1200, drift: 0.02, spread: 0.12 }),
  choreography({ id: "procession", intensity: 0.55, parallax: 0.32, particleDensity: 0.38, durationMs: 7200, staggerMs: 620, drift: 0.44, spread: 0.35 }),
  choreography({ id: "rainfall", intensity: 0.68, parallax: 0.5, particleDensity: 0.78, durationMs: 4800, staggerMs: 130, drift: 0.7, spread: 0.72 }),
  choreography({ id: "lantern-drift", intensity: 0.38, parallax: 0.62, particleDensity: 0.3, durationMs: 12000, staggerMs: 760, drift: 0.58, spread: 0.66 }),
  choreography({ id: "tidepool", intensity: 0.42, parallax: 0.25, particleDensity: 0.52, durationMs: 8400, staggerMs: 340, drift: 0.36, spread: 0.8 }),
  choreography({ id: "flock", intensity: 0.76, parallax: 0.72, particleDensity: 0.62, durationMs: 5200, staggerMs: 90, drift: 0.88, spread: 0.58 }),
  choreography({ id: "pulse", intensity: 0.82, parallax: 0.28, particleDensity: 0.48, durationMs: 3600, staggerMs: 240, drift: 0.2, spread: 0.42 }),
  choreography({ id: "spiral", intensity: 0.7, parallax: 0.9, particleDensity: 0.58, durationMs: 7600, staggerMs: 150, drift: 0.92, spread: 0.76 }),
  choreography({ id: "cascade", intensity: 0.86, parallax: 0.58, particleDensity: 0.84, durationMs: 4300, staggerMs: 70, drift: 0.78, spread: 0.94 }),
  choreography({ id: "echo", intensity: 0.3, parallax: 0.46, particleDensity: 0.24, durationMs: 10000, staggerMs: 980, drift: 0.26, spread: 0.56 }),
]);

export interface AtlasPair {
  readonly themeId: ThemeId;
  readonly choreographyId: AtlasChoreographyId;
}

export const ATLAS_PAIRS: readonly AtlasPair[] = Object.freeze(
  THEME_DEFINITIONS.flatMap((themeDefinition) =>
    CHOREOGRAPHY_DEFINITIONS.map((choreographyDefinition) =>
      Object.freeze({
        themeId: themeDefinition.id,
        choreographyId: choreographyDefinition.id,
      }),
    ),
  ),
);

export const SUMMER_RAIN_DEFAULT = Object.freeze({
  themeId: "washi-dawn" as const,
  choreographyId: "courtyard" as const,
});

export const AVAILABILITY_PROFILES = Object.freeze(["open", "demo-staggered"] as const);
export const DEMO_STAGGERED_TIERS = Object.freeze([4, 8, 13] as const);

export type AvailabilityProfile = (typeof AVAILABILITY_PROFILES)[number];

export interface AvailabilityContent {
  readonly poemIds: readonly string[];
  readonly actionIds: readonly string[];
  readonly playModes: readonly string[];
  readonly editorModes: readonly string[];
  readonly accessibility: readonly string[];
}

export interface AvailabilityOptions {
  readonly profile?: unknown;
  readonly seed?: string | number;
}

export interface AvailabilityResult extends AvailabilityContent {
  readonly profile: AvailabilityProfile;
  readonly tier: (typeof DEMO_STAGGERED_TIERS)[number];
  readonly themeIds: readonly ThemeId[];
  readonly choreographyIds: readonly AtlasChoreographyId[];
}

const ALL_THEME_IDS = Object.freeze(THEME_DEFINITIONS.map((definition) => definition.id));
const ALL_CHOREOGRAPHY_IDS = Object.freeze(
  CHOREOGRAPHY_DEFINITIONS.map((definition) => definition.id),
);

const immutableCopy = <T>(values: readonly T[]): readonly T[] => Object.freeze([...values]);

const isAvailabilityProfile = (value: unknown): value is AvailabilityProfile =>
  AVAILABILITY_PROFILES.some((profile) => profile === value);

export function resolveAvailability(
  content: AvailabilityContent,
  options: AvailabilityOptions = {},
): AvailabilityResult {
  const profile = isAvailabilityProfile(options.profile) ? options.profile : "open";
  const tier = profile === "open"
    ? 13
    : DEMO_STAGGERED_TIERS[
      deriveSeed("availability/demo-staggered/v1", options.seed ?? 0) % DEMO_STAGGERED_TIERS.length
    ];

  return Object.freeze({
    profile,
    tier,
    themeIds: immutableCopy(ALL_THEME_IDS.slice(0, tier)),
    choreographyIds: immutableCopy(ALL_CHOREOGRAPHY_IDS.slice(0, tier)),
    poemIds: immutableCopy(content.poemIds),
    actionIds: immutableCopy(content.actionIds),
    playModes: immutableCopy(content.playModes),
    editorModes: immutableCopy(content.editorModes),
    accessibility: immutableCopy(content.accessibility),
  });
}
